/**
 * Add an attribute definition to a category (BON-161). Handles the
 * required-on-existing handshake: if existing items would violate a new
 * required attribute, the backend returns a count and we confirm forceCreate.
 */
import { useState } from 'react';

import { addAttributeDefinition } from '../../../api/inventory-catalog';

import ModalShell from './ModalShell';

type AttributeDataTypeOption = 'string' | 'number' | 'enum' | 'duration_days' | 'boolean';

const inputClass =
  'mt-1 block w-full rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] px-3 py-2 text-sm text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-moss-500';

const DATA_TYPES: AttributeDataTypeOption[] = [
  'string',
  'number',
  'enum',
  'duration_days',
  'boolean',
];

interface AttrFieldsProps {
  name: string;
  dataType: AttributeDataTypeOption;
  isRequired: boolean;
  allowedValues: string;
  onName: (v: string) => void;
  onDataType: (v: AttributeDataTypeOption) => void;
  onRequired: (v: boolean) => void;
  onAllowedValues: (v: string) => void;
}

function AttrFields(p: AttrFieldsProps) {
  return (
    <div className="flex flex-col gap-3">
      <label className="block">
        <span className="text-sm font-medium text-stone-700 dark:text-stone-300">Name</span>
        <input
          className={inputClass}
          value={p.name}
          onChange={(e) => p.onName(e.target.value)}
          autoFocus
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium text-stone-700 dark:text-stone-300">Type</span>
        <select
          className={inputClass}
          value={p.dataType}
          onChange={(e) => p.onDataType(e.target.value as AttributeDataTypeOption)}
        >
          {DATA_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
      {p.dataType === 'enum' && (
        <label className="block">
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Allowed values <span className="text-stone-400">(comma-separated)</span>
          </span>
          <input
            className={inputClass}
            value={p.allowedValues}
            onChange={(e) => p.onAllowedValues(e.target.value)}
          />
        </label>
      )}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={p.isRequired}
          onChange={(e) => p.onRequired(e.target.checked)}
        />
        <span className="text-sm text-stone-700 dark:text-stone-300">Required</span>
      </label>
    </div>
  );
}

function Notice({ violations, error }: { violations: number | null; error: string | null }) {
  return (
    <>
      {violations !== null && (
        <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
          {violations} existing item(s) do not have this attribute set. Add it anyway? Those items
          will be flagged until updated.
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </>
  );
}

export default function AddAttributeModal({
  category,
  onClose,
  onAdded,
}: {
  category: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [name, setName] = useState('');
  const [dataType, setDataType] = useState<AttributeDataTypeOption>('string');
  const [isRequired, setIsRequired] = useState(false);
  const [allowedValues, setAllowedValues] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [violations, setViolations] = useState<number | null>(null);

  async function submit(force: boolean) {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await addAttributeDefinition(category, {
        name: name.trim(),
        dataType,
        isRequired,
        forceCreate: force,
        allowedValues:
          dataType === 'enum'
            ? allowedValues
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : undefined,
      });
      if (!result.created) {
        setViolations(result.existingViolationsCount);
        return;
      }
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add attribute.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell
      title={`Add attribute · ${category}`}
      submitLabel={violations !== null ? 'Add anyway' : saving ? 'Adding…' : 'Add'}
      saving={saving}
      onClose={onClose}
      onSubmit={() => submit(violations !== null)}
    >
      <AttrFields
        name={name}
        dataType={dataType}
        isRequired={isRequired}
        allowedValues={allowedValues}
        onName={setName}
        onDataType={setDataType}
        onRequired={setIsRequired}
        onAllowedValues={setAllowedValues}
      />
      <Notice violations={violations} error={error} />
    </ModalShell>
  );
}
